import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { bookedAlready, bookingRefused } from "../../src/booking.ts";
import { bookingOpens, tripStart } from "../../src/days.ts";
import { hasDay } from "../../src/plan-day.ts";
import type { Tier } from "../../src/tiers.ts";
import { SETTLED } from "../../src/tobook.ts";
import { AddPaymentForm, ChargeDue, RemovePayment } from "./AddPayment.tsx";
import { AddTodoForm } from "./AddTodo.tsx";
import { AskAi } from "./AskAi.tsx";
import { AskCard, askedOf } from "./Asks.tsx";
import type { Ask, Item, Link, Note, Person, TripFile, TripView } from "./api.ts";
import { type CardEdit, cardAsks, cardEdits, plannable, takesCity, takesPayment } from "./ask-ai.ts";
import { FilesAndLinks, LEAVE_OUT } from "./CardLinks.tsx";
import { CardSection, Glance, glanceLines, JUMP_FROM, JumpRow, type SectionKey } from "./CardSections.tsx";
import {
  ago,
  booksLine,
  itemKind,
  itemWhen,
  km,
  localToday,
  originLabel,
  personName,
  picksLabel,
  STATUS,
  sentence,
  waitingSuggestion,
  whoBooks,
  whoWants,
  whoWantsParts,
} from "./format.ts";
import { everyone, membersNow } from "./former.tsx";
import { distanceM, type LngLat, onMapTypes, point } from "./geo.ts";
import { useDrawsHeic } from "./heic.ts";
import { useKept } from "./kept.ts";
import { bookingLabel, itemLinks, linkRows } from "./links.ts";
import { NEARBY_M } from "./map/common.ts";
import { AddNote, NoteList } from "./Notes.tsx";
import { PhotoStrip, showsAsPhoto } from "./Photos.tsx";
import { PlanDayForm, TakeOffDay } from "./PlanDay.tsx";
import { chargeFacts, paymentFacts, policySteps } from "./paid.ts";
import { RateMine } from "./Rate.tsx";
import { RemoveFromTrip } from "./RemoveFromTrip.tsx";
import { type Finding, ResearchTile } from "./Research.tsx";
import { RichText } from "./RichText.tsx";
import { useReadOnly } from "./readOnly.ts";
import { StayCity } from "./StayCity.tsx";
import { GettingThere, hasGettingThere, hasWhatItsLike, WhatItsLike } from "./StayInfo.tsx";
import { AvatarStack, Button, Chip, Icon, type IconName } from "./souvenir.ts";
import { type Removed, TodoEdit, TodoRow, todoOrder } from "./Todos.tsx";
import { factLine, KindIcon, SectionTitle } from "./ui.tsx";
import { useViewing } from "./viewing.ts";

// What the side panel (desktop) or the layer over the map (phone) shows of one item, in full; and a
// selected place in brief, at the foot of the map. The whole trip's list is MapList.tsx. In full, it reads in
// sections (DESIGN.md, "A card reads in sections"; CardSections.tsx): its head, At a glance, its buttons, the jump
// row, then Questions, Booking, To-dos, Getting there, What it's like, Notes, Research and Files and links, each only
// when it has something, then its choice's other options, what's near it, who added it, and Remove from trip.

interface Props {
  trip: TripView;
  items: Item[];
  // Another thing in the trip, opened from this one's card (what's near it, its options, its to-dos).
  onSelect: (id: string) => void;
  // After I write a note here: load the trip again.
  onChanged: () => void;
}

export const faces = (item: Item, members: Person[]) =>
  item.interest.filter((i) => i.tier && i.tier !== "no").flatMap((i) => members.filter((m) => m.id === i.person));

// The card's Booking (DESIGN.md, "A card reads in sections"), each line with what it says: one fact, or several on
// one line (factLine). While it's still to book, that it needs booking, through whom and who's booking it ("Mei books",
// "nobody's booking it yet"), and when booking opens; else who it's with and who booked it. Then a cancellation's
// every step when it has more than one (where it stands today is At a glance), each payment with how ("SGD 609.93 paid
// by Meiwin · Mastercard 8682") and what's charged later. Its references, its cancellation as it stands and its price
// are At a glance, never here too.
// A line: its icon and words; a payment's place in `paid` (`paid`, and `payment` when it's mine, to take off); a
// charge later's in `charges` (`charge`; `mine`, to take off; `due`, its day come, to confirm); a policy's `steps`.
interface Fact {
  icon: IconName;
  text: string | string[];
  paid?: number;
  payment?: number;
  charge?: number;
  mine?: boolean;
  due?: boolean;
  steps?: { text: string; past: boolean; now: boolean }[];
}

const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });

function bookingFacts(item: Item, books: string | null, trip: TripView, today: string): Fact[] {
  const out: Fact[] = [];
  const people = everyone(trip);
  const held = ["booked", "done", "cancelled"].includes(item.status);
  if (books) {
    out.push({
      icon: "lock",
      text: ["Needs booking", item.booking?.provider, books].filter((p): p is string => Boolean(p)),
    });
  } else if (item.booking?.provider || (held && item.booking?.booked_by)) {
    // Who booked it only once it's been booked (booked, done, or cancelled since): an option back to being weighed
    // keeps it for next time (Not booked after all), and says only who it's through.
    const by = held ? personName(item.booking.booked_by, people, trip.you) : null;
    const parts = [
      item.booking.provider && `through ${item.booking.provider}`,
      by && `booked by ${by === "You" ? "you" : by}`,
    ].filter((p): p is string => Boolean(p));
    // First on its line, a capital: "Booked by you", "Through Expedia · booked by you", "Through Pelago" (a name alone
    // beside the lock read as a word with nothing said of it: the founder's Haneda transfer, "Pelago").
    out.push({ icon: "lock", text: parts.map((p, n) => (n === 0 ? sentence(p) : p)) });
  } else if (item.booking_needed && !held) out.push({ icon: "lock", text: "Needs booking" });
  // When booking opens, while it's still to book: "Booking opens 25 Oct", or that it's open.
  const opens = held ? null : bookingOpens(item.books_from, item.when, tripStart(trip.dates));
  if (opens)
    out.push({
      icon: "calendar",
      text: opens > today ? `Booking opens ${DAY.format(new Date(`${opens}T00:00:00Z`))}` : "Booking open now",
    });
  const steps = item.status === "booked" ? policySteps(item) : [];
  if (steps.length > 0) out.push({ icon: "replay", text: "Cancellation", steps });
  // A payment of mine says which it is (its place in the list), to be taken off on the web.
  const mine = new Set([trip.you, trip.members.find((m) => m.id === trip.you)?.member]);
  for (const [n, p] of (item.paid ?? []).entries())
    out.push({
      icon: "check",
      text: paymentFacts(p, people, trip.you),
      paid: n,
      ...(mine.has(p.by) ? { payment: n } : {}),
    });
  // What's charged later, under what's paid (features/bookings-and-costs.md, "Charged later").
  for (const [n, c] of (item.charges ?? []).entries()) {
    const said = chargeFacts(c, people, trip.you, today);
    out.push({ icon: "clock", text: said.text, charge: n, mine: mine.has(c.by), due: said.due });
  }
  return out;
}

export function Details({
  item,
  trip,
  items,
  links,
  notes,
  asks,
  research,
  onRead,
  files,
  invited,
  bar,
  focus = false,
  choice = null,
  options = [],
  todos = [],
  onSelect,
  onChanged,
  onRemoved,
  onAnswer,
  onBook,
  start = null,
  onStarted,
}: Props & {
  // A change chosen from this card's small card (its "Change with your AI"), to start once it's open in full.
  start?: CardEdit | null;
  onStarted?: () => void;
  // Its booking form opens (TripPage: over this card): "Mark as booked" or "Edit booking" on what can be
  // booked on the web; "unbook", at its Not booked after all.
  onBook?: ((start?: "unbook") => void) | undefined;
  // Opened from the deck: my rating here answers it (RateMine).
  onAnswer?: ((tier: Tier) => boolean) | undefined;
  item: Item;
  // The to-dos for it (their parent): listed on its card, each with its tick.
  todos?: Item[];
  // A to-do removed from its own card, or it removed from the trip (RemoveFromTrip.tsx).
  onRemoved: (removed: Removed) => void;
  // The choice it's an option of (or is), and that choice's options: listed on its card.
  choice?: Item | null;
  options?: Item[];
  links: Link[];
  notes: Note[];
  asks: Ask[];
  // The research about it, as tiles; one opens to be read in full (over the view, TripPage).
  research: Finding[];
  onRead: (id: string) => void;
  files: TripFile[];
  // Opened from an AI's upload link, for a file or a photo: that upload shows ready.
  invited: "file" | "photo" | null;
  // What its head holds on a computer: Share and its close, and over another view its way to the map. On a
  // phone the bar above the card has them.
  bar?: ReactNode;
  // Its title takes the focus as it opens, so it's read from there (and Tab goes on into it).
  focus?: boolean;
}) {
  const origin = originLabel(item);
  const status =
    item.type === "choice"
      ? choiceStatus(options)
      : (STATUS[item.status] ?? { label: item.status, icon: "note" as const });
  // At a glance's lines; its when in the head only when they don't give it (a stay's check-in and check-out, a
  // journey's leaving and arriving with their times, a place's or goal's day and time), so the dates show once.
  const glance = glanceLines(item, trip);
  const when = glance.some((l) => l.when) ? null : itemWhen(item.when);
  const at = point(item);
  // A choice's options are listed on their own (below), not again as nearby.
  const listed = new Set(options.map((o) => o.id));
  const nearby = at
    ? items
        .filter((i) => i.id !== item.id && i.type !== "move" && !listed.has(i.id))
        .map((i) => ({ i, d: point(i) ? distanceM(at, point(i) as LngLat) : Number.POSITIVE_INFINITY }))
        .filter(({ d }) => d <= NEARBY_M)
        .sort((a, b) => a.d - b.d)
    : [];
  // Who's booking it, while it still needs booking: its booker, else the owner of its to-do; one who has
  // left the trip is nobody (format.ts, `whoBooks`). A to-do's own For says who does it.
  const toBook = item.type !== "task" && item.booking_needed && !SETTLED.includes(item.status);
  const books = toBook
    ? booksLine(
        whoBooks(
          item,
          todos.find((t) => t.status !== "done"),
          membersNow(trip),
        ),
        trip.members,
        trip.you,
      )
    : null;
  const today = localToday();
  const booking = bookingFacts(item, books, trip, today);
  const changedBy = item.updated.by.id !== item.created.by.id || item.updated.at !== item.created.at;
  const shown = faces(item, trip.members);
  // A read-only trip: Files and Notes only with something in them (nothing can be added), and nothing asks
  // your AI for a change it would be refused.
  const readOnly = useReadOnly();
  // Places and goals are wanted (or not) by each of us; stays and moves are everyone's, except an option
  // still being compared, which I rate too.
  const wanted = item.type === "place" || item.type === "goal";
  const who = (by: { id: string; name: string }) => (by.id === trip.you ? "you" : by.name);
  // What a choice and its options are, when they aren't in the plan: said on the card.
  const open = item.status === "option" || item.status === "not_chosen";
  const about =
    item.type === "choice"
      ? options.length === 0
        ? readOnly
          ? "Nothing to choose from."
          : "Nothing to choose from yet. Ask your AI to find some options."
        : options.some((o) => point(o))
          ? "Its options are on the map, drawn dashed, until one is chosen."
          : null
      : choice && item.status === "option"
        ? `One of the options for ${choice.title}, not in the plan until it's chosen.`
        : choice && item.status === "not_chosen"
          ? `Not chosen for ${choice.title}.`
          : null;
  // Somewhere on the map, but no coordinates yet (a choice shows by its options instead).
  const unplaced = !at && item.type !== "choice" && onMapTypes.has(item.type) && item.status !== "cancelled";
  const others = options.filter((o) => o.id !== item.id);
  // The group's own photos show at the top (those this browser draws); the rest are the card's files.
  const drawsHeic = useDrawsHeic();
  const plain = files.filter((f) => !showsAsPhoto(f, drawsHeic));
  // Add a photo, on what has a picture: a place, a stay, an option (and wherever an AI's link asks).
  const photos =
    item.type === "place" ||
    item.type === "stay" ||
    item.status === "option" ||
    item.status === "not_chosen" ||
    invited === "photo";
  // Booked on the web from here (DESIGN.md, Views, "Record a booking where it's made"): a stay, a journey, a
  // choice's option, or a place that needs booking or is booked; booked already (or done, with its booking), its
  // booking to edit.
  const bookable = bookingRefused(item, choice && item.parent === choice.id ? "choice" : undefined) === null;
  const booked = bookedAlready(item);
  // A to-do's card says whether it's done with its own tick, not a status chip.
  const task = item.type === "task";
  const chips = !task || origin || when;
  const title = useRef<HTMLHeadingElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: again for each item opened in it
  useEffect(() => {
    if (focus) title.current?.focus({ preventScroll: true });
  }, [focus, item.id]);
  // Plan for a day (PlanDay.tsx), on a place or goal still wanted or in the plan: its form opens in the card, under
  // the card's other actions' place, and closing it puts the focus back on what opened it.
  const planning = plannable(item);
  // Add a payment (AddPayment.tsx), on any card but a choice's own: its form opens in the same place.
  const paying = takesPayment(item);
  const [editing, setEditing] = useState<"plan" | "pay" | "todo" | null>(null);
  const actions = useRef<HTMLDivElement>(null);
  const [refocus, setRefocus] = useState<"plan" | "pay" | "todo" | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: again for each item opened in it
  useEffect(() => setEditing(null), [item.id]);
  useEffect(() => {
    if (!refocus || editing !== null) return;
    setRefocus(null);
    actions.current
      ?.querySelector<HTMLElement>(
        refocus === "plan" ? ".tm-plan-button" : refocus === "pay" ? ".tm-pay-button" : ".tm-ask-ai-button",
      )
      ?.focus({ preventScroll: true });
  }, [refocus, editing]);
  // What this card changes on the web, as "Change with your AI" lists them (ask-ai.ts), and doing one: the
  // card's own forms open, or its control for it takes the focus (and a file picker opens), scrolled into view.
  const article = useRef<HTMLElement>(null);
  const edits = cardEdits(item, {
    choice,
    books: Boolean(onBook),
    asks: asks.filter((a) => askedOf(a, trip.you)).length,
    mine: asks.filter((a) => a.status === "open" && a.by.id === trip.you).length,
    links: linkRows(item, links).length,
    readOnly,
  });
  const reach = (edit: CardEdit, then?: (el: HTMLElement) => void) => {
    const el = article.current?.querySelector<HTMLElement>(
      edit === "rate" ? '[data-edit="rate"] button' : edit === "answer" ? ANSWER : `[data-edit="${edit}"]`,
    );
    if (!el) return;
    el.scrollIntoView({
      block: "center",
      behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
    if (then) then(el);
    else el.focus({ preventScroll: true });
  };
  const run = (edit: CardEdit) => {
    if (edit === "book") onBook?.();
    else if (edit === "unbook") onBook?.("unbook");
    else if (edit === "plan") setEditing("plan");
    else if (edit === "pay") setEditing("pay");
    else if (edit === "todo-add") setEditing("todo");
    else if (edit === "links")
      // Its links as rows to change, the first's Edit taking the focus.
      reach(edit, (el) => {
        if (el.getAttribute("aria-pressed") !== "true") el.click();
        requestAnimationFrame(() =>
          article.current
            ?.querySelector<HTMLElement>('.tm-link-row [data-link-action="edit"]')
            ?.focus({ preventScroll: true }),
        );
      });
    else if (
      edit === "file" ||
      edit === "photo" ||
      edit === "link-add" ||
      edit === "city" ||
      edit === "todo-done" ||
      edit === "todo-important" ||
      edit === "todo-remove" ||
      edit === "remove"
    )
      reach(edit, (el) => el.click());
    else if (edit === "note")
      reach(edit, (el) => {
        el.click();
        requestAnimationFrame(() =>
          article.current?.querySelector<HTMLElement>(".tm-note-form textarea")?.focus({ preventScroll: true }),
        );
      });
    else reach(edit);
  };
  const runRef = useRef(run);
  runRef.current = run;
  // biome-ignore lint/correctness/useExhaustiveDependencies: once for each change asked from the small card
  useEffect(() => {
    if (!start) return;
    // Once the card is drawn; heard as started then, so its own change of state can't call it off.
    const frame = requestAnimationFrame(() => {
      runRef.current(start);
      onStarted?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [start]);
  // Nothing to change on a shared trip's view: no "Change with your AI".
  const viewing = useViewing();
  const ask = viewing ? null : (
    <AskAi trip={trip} thing={item.title} edits={edits} asks={cardAsks(item, choice)} onEdit={run} look="card" />
  );
  // Its sections (DESIGN.md, "A card reads in sections"), in their order, each only when it has something: Notes and
  // Files and links whenever something can be added there.
  const uid = useId();
  const sectionId = (key: SectionKey) => `${uid}-${key}`;
  const linked = itemLinks(item, links, LEAVE_OUT).length;
  const page = item.booking?.url;
  // Questions about it, those waiting on me first.
  const waitsOnMe = (a: Ask) => !readOnly && a.waiting_on.some((p) => p.id === trip.you);
  const sections: { key: SectionKey; count?: number; edit?: string; body: ReactNode }[] = [];
  if (asks.length > 0)
    sections.push({
      key: "questions",
      count: asks.length,
      edit: "answer",
      body: [...asks]
        .sort((a, b) => Number(waitsOnMe(b)) - Number(waitsOnMe(a)))
        .map((a) => <AskCard key={a.id} ask={a} trip={trip} onChanged={onChanged} />),
    });
  if (booking.length > 0 || page)
    sections.push({
      key: "booking",
      body: (
        <>
          {booking.length > 0 && (
            <BookingLines
              item={item}
              trip={trip}
              facts={booking}
              onBook={onBook}
              bookable={bookable}
              onChanged={onChanged}
            />
          )}
          {page && (
            <ul className="tm-contact tm-item-links" aria-label="Its booking page">
              <li className="tm-item-link-item">
                <a
                  className="caption tm-contact-link tm-item-link"
                  href={page}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <span className="tm-item-link-label">{bookingLabel(item)}</span>
                </a>
              </li>
            </ul>
          )}
        </>
      ),
    });
  if (todos.length > 0)
    sections.push({
      key: "todos",
      count: todos.filter((t) => t.status !== "done").length,
      body: (
        <ul className="tm-rows">
          {[...todos].sort(todoOrder).map((t) => (
            <TodoRow key={t.id} item={t} trip={trip} onOpen={(id) => onSelect(id)} />
          ))}
        </ul>
      ),
    });
  if (hasGettingThere(item)) sections.push({ key: "there", body: <GettingThere item={item} /> });
  if (hasWhatItsLike(item)) sections.push({ key: "like", body: <WhatItsLike item={item} /> });
  if (item.notes || notes.length > 0 || !readOnly)
    sections.push({
      key: "notes",
      count: notes.length,
      body: (
        <>
          {/* Its own notes, what the AIs wrote, then people's. */}
          {item.notes && <RichText text={item.notes} className="body tm-notes" />}
          {notes.length > 0 && (
            <NoteList notes={notes} members={trip.members} you={trip.you} edit={{ trip: trip.id, onChanged }} />
          )}
          <AddNote key={item.id} on={item.id} edit={{ trip: trip.id, onChanged }} />
        </>
      ),
    });
  if (research.length > 0)
    sections.push({
      key: "research",
      count: research.length,
      body: <CardResearch key={`research-${item.id}`} item={item.id} findings={research} trip={trip} onRead={onRead} />,
    });
  if (linked > 0 || plain.length > 0 || !readOnly)
    // No count: two kinds of thing, its pills and its files, each plain to see.
    sections.push({
      key: "files",
      body: (
        <FilesAndLinks
          key={`files-${item.id}`}
          item={item}
          trip={trip}
          items={items}
          links={links}
          files={plain}
          invited={invited}
          photos={photos}
          onChanged={onChanged}
        />
      ),
    });
  return (
    <article className="tm-details" ref={article}>
      {bar && <div className="tm-details-bar">{bar}</div>}
      <PhotoStrip key={`photos-${item.id}`} item={item} trip={trip.id} you={trip.you} onChanged={onChanged} />
      <header className="tm-details-head">
        <span className="caption tm-muted">{itemKind(item)}</span>
        <h1 className="display-m" ref={title} {...(focus && { tabIndex: -1 })}>
          {item.title}
        </h1>
        {takesCity(item) && <StayCity item={item} trip={trip.id} onChanged={onChanged} />}
      </header>
      {chips && (
        <div className="tm-chips">
          {!task && (
            <Chip icon={status.icon} {...(status.tone ? { tone: status.tone } : {})}>
              {status.label}
            </Chip>
          )}
          {origin && <Chip icon="star">{origin}</Chip>}
          {when && <Chip icon="calendar">{when}</Chip>}
        </div>
      )}
      {(about || unplaced) && (
        <div className="tm-details-about">
          {about && <p className="callout tm-muted">{about}</p>}
          {unplaced && (
            <p className="callout tm-not-placed">
              <Icon name="pin" size={18} />
              <span>
                {readOnly ? (
                  "Not on the map: it has no coordinates."
                ) : (
                  <>
                    Not on the map yet: it has no coordinates.{" "}
                    {open ? "Ask your AI to look them up." : "Tripmo asks your AI to look them up, or ask it now."}
                  </>
                )}
              </span>
            </p>
          )}
        </div>
      )}
      {item.recommended_by && item.recommended_by.length > 0 && (
        <ul className="tm-recs" aria-label="Recommended by">
          {item.recommended_by.map((r) => (
            <li key={r.name} className="tm-rec">
              <span className="callout tm-rec-who">
                <Icon name="heart" size={16} />
                <span>
                  <strong>{r.name}</strong> recommends
                </span>
              </span>
              {r.said && <q className="quote tm-rec-said">{r.said}</q>}
            </li>
          ))}
        </ul>
      )}
      {(wanted || item.status === "option") && (
        <div className="tm-mine">
          {wanted && (
            <div className="tm-who">
              {shown.length > 0 && <AvatarStack people={shown} size={32} />}
              <span className="callout">{factLine(whoWantsParts(item, trip.members, trip.you))}</span>
            </div>
          )}
          <RateMine
            item={item}
            trip={trip.id}
            you={trip.you}
            onChanged={onChanged}
            {...(onAnswer ? { onAnswer } : {})}
          />
        </div>
      )}
      <Glance lines={glance} />
      {editing === null && (
        <div className="tm-card-actions" ref={actions}>
          {planning &&
            !readOnly &&
            (hasDay(item.when) ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  icon="calendar"
                  className="tm-plan-button"
                  aria-label="Move to another day"
                  onClick={() => setEditing("plan")}
                >
                  Move
                </Button>
                <TakeOffDay item={item} trip={trip} onChanged={onChanged} />
              </>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                icon="calendar"
                className="tm-plan-button"
                onClick={() => setEditing("plan")}
              >
                Plan for a day
              </Button>
            ))}
          {paying && !readOnly && (
            <Button
              variant="secondary"
              size="sm"
              icon="plus"
              className="tm-pay-button"
              onClick={() => setEditing("pay")}
            >
              Add a payment
            </Button>
          )}
          {onBook && bookable && !readOnly && (
            <Button
              variant={booked ? "quiet" : "secondary"}
              size="sm"
              icon="lock"
              className={`tm-book-button${booked ? " tm-quiet-action" : ""}`}
              onClick={() => onBook()}
            >
              {booked ? "Edit booking" : "Mark as booked"}
            </Button>
          )}
          {ask}
        </div>
      )}
      {editing === "pay" && (
        <AddPaymentForm
          key={`pay-${item.id}`}
          item={item}
          trip={trip}
          items={items}
          onChanged={onChanged}
          onClose={() => {
            setEditing(null);
            setRefocus("pay");
          }}
        />
      )}
      {editing === "todo" && (
        <AddTodoForm
          key={`todo-${item.id}`}
          trip={trip}
          about={item}
          onChanged={onChanged}
          onClose={() => {
            setEditing(null);
            setRefocus("todo");
          }}
        />
      )}
      {editing === "plan" && (
        <PlanDayForm
          key={`plan-${item.id}`}
          item={item}
          trip={trip}
          items={items}
          onChanged={onChanged}
          onClose={() => {
            setEditing(null);
            setRefocus("plan");
          }}
        />
      )}
      {/* A to-do's own card: its tick, who it's for, when it's due, whether it's important, Remove. */}
      {task && <TodoEdit key={`todo-edit-${item.id}`} item={item} trip={trip} onRemoved={onRemoved} />}
      {sections.length >= JUMP_FROM && (
        <JumpRow key={`jump-${item.id}`} sections={sections.map((x) => ({ key: x.key, id: sectionId(x.key) }))} />
      )}
      {sections.map((x) => (
        <CardSection
          key={x.key}
          id={sectionId(x.key)}
          section={x.key}
          {...(x.count !== undefined ? { count: x.count } : {})}
          {...(x.edit ? { edit: x.edit } : {})}
        >
          {x.body}
        </CardSection>
      ))}
      {/* A choice's options, and on each of them (the chosen one too, which may have no row on Bookings when
          nothing is left to book) the others. */}
      {choice && others.length > 0 && (
        <section className="tm-nearby" aria-labelledby="choice-options">
          <SectionTitle id="choice-options" count={others.length}>
            {item.type === "choice" ? "Options" : `Other options for ${choice.title}`}
          </SectionTitle>
          <div className="tm-nearby-row">
            {others.map((o) => (
              <button key={o.id} type="button" className="tm-near" onClick={() => onSelect(o.id)}>
                <KindIcon item={o} size={16} className="tm-near-icon" />
                <span className="callout">{o.title}</span>
                <span className="caption tm-muted">{STATUS[o.status]?.label ?? o.status}</span>
              </button>
            ))}
          </div>
        </section>
      )}
      {nearby.length > 0 && (
        <section className="tm-nearby" aria-labelledby="nearby">
          <h2 id="nearby" className="title">
            Near it in the trip
          </h2>
          <div className="tm-nearby-row">
            {nearby.map(({ i, d }) => (
              <button key={i.id} type="button" className="tm-near" onClick={() => onSelect(i.id)}>
                <KindIcon item={i} size={16} className="tm-near-icon" />
                <span className="callout">{i.title}</span>
                <span className="caption tm-muted">{km(d)}</span>
              </button>
            ))}
          </div>
        </section>
      )}
      <p className="caption tm-muted tm-provenance">
        Added by {who(item.created.by)} · {ago(item.created.at)}
        {changedBy && ` · changed by ${who(item.updated.by)} ${ago(item.updated.at)}`}
      </p>
      {/* Last of all, on everything but a to-do (its own Remove to-do is above): Remove from trip. */}
      {!task && !readOnly && (
        <RemoveFromTrip item={item} trip={trip} options={options} onRemoved={onRemoved} onChanged={onChanged} />
      )}
    </article>
  );
}

// A card's Booking lines (bookingFacts): each with its icon, a payment of mine with its X, a charge later whose day
// has come with Paid and Edit, a policy's steps one under another, the one past its day quieter.
function BookingLines({
  item,
  trip,
  facts,
  onBook,
  bookable,
  onChanged,
}: {
  item: Item;
  trip: TripView;
  facts: Fact[];
  onBook?: ((start?: "unbook") => void) | undefined;
  bookable: boolean;
  onChanged: () => void;
}) {
  const readOnly = useReadOnly();
  return (
    <ul className="tm-facts">
      {facts.map((f) => (
        <li
          key={
            f.paid !== undefined
              ? `paid-${f.paid}`
              : f.charge !== undefined
                ? `charge-${f.charge}`
                : [f.text].flat().join()
          }
          className="callout"
        >
          <Icon name={f.icon} size={20} />
          {f.steps ? (
            <ul className="tm-steps" aria-label="Cancellation">
              {f.steps.map((step) => (
                <li key={step.text} className={step.past ? "tm-muted" : step.now ? "is-now" : ""}>
                  {step.text}
                </li>
              ))}
            </ul>
          ) : f.charge !== undefined && f.due && !readOnly ? (
            // Its day has come: the question, and Paid and Edit under it.
            <span className="tm-fact-stack">
              <span>{factLine([f.text].flat())}</span>
              <ChargeDue
                item={item}
                trip={trip}
                at={f.charge}
                words={[f.text].flat().join(" · ")}
                onChanged={onChanged}
                onEdit={onBook && bookable ? () => onBook() : undefined}
              />
            </span>
          ) : (
            <span>{typeof f.text === "string" ? f.text : factLine(f.text)}</span>
          )}
          {f.payment !== undefined && !readOnly && (
            <RemovePayment
              item={item}
              trip={trip}
              at={f.payment}
              words={[f.text].flat().join(" · ")}
              onChanged={onChanged}
            />
          )}
          {f.charge !== undefined && f.mine && !readOnly && (
            <RemovePayment
              item={item}
              trip={trip}
              at={f.charge}
              charge
              words={[f.text].flat().join(" · ")}
              onChanged={onChanged}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

// Where answering a question starts: its first choice, else its line of my own.
const ANSWER = [
  '[data-edit="answer"] .tm-ask-options button',
  '[data-edit="answer"] textarea',
  '[data-edit="answer"] input',
].join(", ");

// A choice is decided once one of its options is picked.
const isPicked = (o: Item) => o.status === "planned" || o.status === "booked" || o.status === "done";
function choiceStatus(options: Item[]): { label: string; icon: IconName; tone?: "moss" } {
  return options.some(isPicked)
    ? { label: "Decided", icon: "check", tone: "moss" }
    : { label: "Not decided", icon: "note" };
}

// What a choice's small card says: decided for what, or how many options.
export function choiceLine(options: Item[]): string {
  const picked = options.find(isPicked);
  if (picked) return `Decided: ${picked.title}`;
  return options.length === 0
    ? "Not decided · no options yet"
    : `Not decided · ${options.length} ${options.length === 1 ? "option" : "options"}`;
}

// The research about an item, under its card's Research: the same tiles as on Ideas, opening the same way; the last
// one opened from this card is lit when it comes back (kept with the card's step, so Back finds it so).
function CardResearch({
  item,
  findings,
  trip,
  onRead,
}: {
  item: string;
  findings: Finding[];
  trip: TripView;
  onRead: (id: string) => void;
}) {
  const [last, setLast] = useKept<string | null>(`card-${item}-read`, null);
  return (
    <div className="tm-research-list">
      {findings.map((f) => (
        <ResearchTile
          key={f.research.id}
          finding={f}
          trip={trip}
          lit={f.research.id === last}
          onRead={(id) => {
            setLast(id);
            onRead(id);
          }}
        />
      ))}
    </div>
  );
}

// The card's way to the map, over another view and over the map on a phone: the map with it selected, in
// brief (DESIGN.md, "An item opens over the view I'm on").
export function ShowOnMap({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="tm-to-map" onClick={onClick}>
      <Icon name="map" size={16} />
      <span>Show on map</span>
    </button>
  );
}

// The card's close: back to where it was opened from (the view, the map's list, the small card), the
// place still selected on the map.
export function CloseCard({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="tm-layer-close" aria-label="Close" onClick={onClick}>
      <Icon name="close" size={20} />
    </button>
  );
}

// A selected place in brief at the bottom of the map (on a phone, above the tabs): tapping it opens the
// card in full. On a computer, a place the pointer rests on shows the same there, without a close: it goes
// when the pointer does.
export function Peek({
  item,
  trip,
  options = [],
  onOpen,
  onClose,
  ask,
}: {
  item: Item;
  trip: TripView;
  // A choice's options, for what it says.
  options?: Item[];
  onOpen: () => void;
  onClose?: () => void;
  // Its "Change with your AI" (AskAi.tsx), beside its close: on a place selected, not one the pointer rests on.
  ask?: ReactNode;
}) {
  const shown = faces(item, trip.members);
  const suggested = waitingSuggestion(item, trip.members);
  const when = itemWhen(item.when);
  const status = STATUS[item.status]?.label ?? item.status;
  return (
    <section className="tm-peek" aria-label={item.title}>
      <button type="button" className="tm-peek-open" onClick={onOpen}>
        <KindIcon item={item} suggested={suggested} />
        <span className="tm-row-text">
          <span className="caption tm-muted">{[itemKind(item), when].filter(Boolean).join(" · ")}</span>
          <span className="title">{item.title}</span>
          <span className="caption tm-muted">
            {item.type === "choice"
              ? choiceLine(options)
              : item.type === "stay" || item.type === "move"
                ? status
                : whoWants(item, trip.members, trip.you)}
            <Picks item={item} />
          </span>
        </span>
        {shown.length > 0 && <AvatarStack people={shown} size={24} max={3} />}
      </button>
      {ask}
      {onClose && (
        <button type="button" className="tm-peek-close" aria-label="Close" onClick={onClose}>
          <Icon name="close" size={16} />
        </button>
      )}
    </section>
  );
}

// " · Jon's pick", in the sun's colour, after a row's other facts.
export function Picks({ item }: { item: Item }) {
  const picks = picksLabel(item);
  return picks ? (
    <>
      {" · "}
      <span className="tm-sun-text">{picks}</span>
    </>
  ) : null;
}
